import React from 'react'
import PropTypes from 'prop-types'
import { Paper } from '../../../../components/common/Containers'
import { SpacingContainer } from '../../../../components/common/Containers/Base'
import { Table } from '../../../../components/common/ResponsiveTable'
import Typography from '../../../../components/common/Typography'
import NothingFoundCardList from '../../../../components/generics/Cards/NothingFoundCard/NothingFoundCardList'
import { useTrans } from '../../../../services/i18n'
import { emptyArray } from '../../../../utils/arrayUtils'
import connect from '../../connect'
import { IconSearch } from '../../../../components/svg/Icons/IconSearch'
import { IconSavedSearches } from '../../../../components/svg/Icons/IconSavedSearches'
import Item from './Item'
import Head from './Item/Head'
import UsageBadge from './Item/UsageBadge'
import { Grid } from '@material-ui/core'
import BusinessIcon from '@material-ui/icons/Business'
import { Divider } from '../../../../components/common/Divider'
import { LoaderSmall } from '../../../../components/common/LoaderSmall'
import { Switch } from '../../../../components/common/Inputs'
import { Button } from '../../../../components/common/Buttons'
import styles from './styles.module.css'
import useBooleanHandlers from '../../../../hooks/useBooleanHandlers'
import ConfirmDialog from '../../../../components/generics/Confirm/Dialog'
import { useSelector } from 'react-redux'
import { selectUserPermissions, isEnabled } from '../../../../selectors'

const SavedSearchesList = ({
  groups = [],
  savedSearches,
  savedSearchesActions,
  userDetail,
  savedSearchesSelected = [],
  addGroupSavedSearches,
  removeGroupSavedSearches,
  addOneSavedSearches,
  removeOneSavedSearches,
  saveDataAfterDeleteSearch,
  saveDataAfterDeleteBulkSearch,
}) => {
  const { trans } = useTrans()
  const permissions = useSelector(selectUserPermissions)
  const { pending } = savedSearches
  const { onTurnOnOffNotifications, onDeleteBulk } = savedSearchesActions
  const [
    openConfirmDeleteBulk,
    onOpenConfirmDeleteBulk,
    onCloseConfirmDeleteBulk,
  ] = useBooleanHandlers()

  const deleteSearchesCount = savedSearchesSelected.length
  const hasItems = !emptyArray(groups)

  const buildNotificationPayload = (overrides = {}) => ({
    idSavedSearchOwner: userDetail.id,
    savedSearchOwner: userDetail.userName,
    pursuitNotices: userDetail.pursuitNotices ? 1 : 0,
    savedSearchNotices: userDetail.savedSearchNotices,
    teamUpdates: userDetail.teamUpdates ? 1 : 0,
    taskReminders: userDetail.taskReminders,
    onEmailNotifications: userDetail.onEmailNotifications,
    pursuitDailyRecap: userDetail.pursuitDailyRecap,
    savedSearchDailyRecap: userDetail.savedSearchDailyRecap,
    teamUpdatesDailyRecap: userDetail.teamUpdatesDailyRecap,
    taskRemindersDailyRecap: userDetail.taskRemindersDailyRecap,
    personalTaskRemindersDailyRecap: userDetail.personalTaskRemindersDailyRecap,
    ...overrides,
  })

  const onToggleSwitch = () => {
    onTurnOnOffNotifications(
      buildNotificationPayload({
        savedSearchNotices: userDetail.savedSearchNotices === 1 ? 0 : 1,
      })
    )
  }

  const onDeleteBulkHandler = () => {
    const params = {
      ids: [...savedSearchesSelected],
      type: 'in',
      savedSearchOwner: userDetail.userName,
    }
    onDeleteBulk(params).then(() => {
      saveDataAfterDeleteBulkSearch(params)
      onCloseConfirmDeleteBulk()
    })
  }

  const getGroupSelectionState = group => {
    const ids = (group.items || []).map(item => item.id)
    const selectedCount = ids.filter(id =>
      savedSearchesSelected.includes(id)
    ).length
    return {
      ids,
      isAllChecked: ids.length > 0 && selectedCount === ids.length,
      isSomeChecked: selectedCount > 0 && selectedCount < ids.length,
    }
  }

  const onSelectGroup = group => {
    const { ids, isAllChecked } = getGroupSelectionState(group)
    if (isAllChecked) {
      removeGroupSavedSearches(ids)
    } else {
      addGroupSavedSearches(ids)
    }
  }

  return (
    <>
      <Paper className="relative" hidden>
        <SpacingContainer px="5sm3" py={2}>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            spacing={3}
            style={{ margin: '0.1rem 0rem' }}
          >
            <Grid item xs zeroMinWidth>
              <Typography variant="h4" weight={500} fontFamily="lexend">
                <IconSavedSearches /> {trans('saved-searches')}
              </Typography>
            </Grid>
            {!pending && hasItems && (
              <>
                {isEnabled(
                  permissions,
                  'registered-users',
                  'saved-searches',
                  'edit'
                ) && (
                  <Grid item className={styles.notificationContainer}>
                    <Typography
                      className={styles.notificationText}
                      weight="600"
                    >
                      Notifications
                    </Typography>
                    <Switch
                      size="small"
                      checked={Boolean(userDetail.savedSearchNotices === 1)}
                      onChange={onToggleSwitch}
                    />
                  </Grid>
                )}
                {isEnabled(
                  permissions,
                  'registered-users',
                  'saved-searches',
                  'delete'
                ) && (
                  <Grid item>
                    <Button
                      disabled={pending || deleteSearchesCount === 0}
                      color={
                        deleteSearchesCount === 0 ? 'disabled' : 'secondary'
                      }
                      variant="outlined"
                      onClick={
                        deleteSearchesCount === 0
                          ? undefined
                          : onOpenConfirmDeleteBulk
                      }
                    >
                      {`Delete Searches (${deleteSearchesCount})`}
                    </Button>
                  </Grid>
                )}
              </>
            )}
          </Grid>

          <SpacingContainer px="0" py={2}>
            <Divider light />
            {pending && <LoaderSmall />}

            {!pending &&
              hasItems &&
              groups.map(group => {
                const { isAllChecked, isSomeChecked } =
                  getGroupSelectionState(group)
                return (
                  <div
                    key={group.organizationId}
                    className={styles.orgGroup}
                  >
                    <div className={styles.orgSubheader}>
                      <div className={styles.orgTitle}>
                        <BusinessIcon
                          className={styles.orgIcon}
                          fontSize="small"
                        />
                        <Typography
                          variant="h6"
                          weight={600}
                          fontFamily="lexend"
                        >
                          {group.organizationName}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="hint"
                          className={styles.orgCount}
                        >
                          ({group.items.length})
                        </Typography>
                      </div>
                      <UsageBadge stats={group.stats} />
                    </div>

                    <Table
                      stickyHeader
                      head={
                        <Head
                          isAllChecked={isAllChecked}
                          isSomeChecked={isSomeChecked}
                          onSelectAll={() => onSelectGroup(group)}
                        />
                      }
                    >
                      {group.items.map(item => (
                        <Item
                          key={item.id}
                          item={item}
                          userDetail={userDetail}
                          pending={pending}
                          isChecked={savedSearchesSelected.includes(item.id)}
                          addOneSavedSearches={addOneSavedSearches}
                          removeOneSavedSearches={removeOneSavedSearches}
                          saveDataAfterDeleteSearch={saveDataAfterDeleteSearch}
                          {...savedSearchesActions}
                        />
                      ))}
                    </Table>
                  </div>
                )
              })}

            {!pending && !hasItems && (
              <NothingFoundCardList
                Icon={<IconSearch notFound />}
                title={trans('no-saved-searches')}
                description={trans('all-saved-searches-will-appear-here')}
              />
            )}
          </SpacingContainer>
        </SpacingContainer>
      </Paper>

      <ConfirmDialog
        open={openConfirmDeleteBulk}
        onClose={onCloseConfirmDeleteBulk}
        onConfirm={onDeleteBulkHandler}
        pending={pending}
        title={trans('delete-saved-search')}
        text={trans('confirm-delete-saved-search-bulk')}
        confirmTitle={trans('delete')}
      >
        <Typography variant="button" color="hint">
          {trans('saved-searches-to-delete')}
        </Typography>
        <Typography color="primary">{deleteSearchesCount}</Typography>
      </ConfirmDialog>
    </>
  )
}

SavedSearchesList.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      organizationName: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
      ),
      stats: PropTypes.shape({
        savedSearchCount: PropTypes.number,
        savedSearchLimit: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string,
        ]),
        savedSearchLimitTier: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string,
        ]),
      }),
    })
  ),
  savedSearches: PropTypes.shape({
    pending: PropTypes.bool,
  }),
  savedSearchesActions: PropTypes.shape({
    onTurnOnOffNotifications: PropTypes.func,
    onDeleteBulk: PropTypes.func,
  }),
  userDetail: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    userName: PropTypes.string,
    pursuitNotices: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    savedSearchNotices: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    teamUpdates: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    savedSearchTypeNotices: PropTypes.string,
    consolidateNotificationSelect: PropTypes.string,
    consolidateNotificationNumber: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    taskReminders: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    onEmailNotifications: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.number,
    ]),
    pursuitDailyRecap: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    savedSearchDailyRecap: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.number,
    ]),
    teamUpdatesDailyRecap: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.number,
    ]),
    taskRemindersDailyRecap: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.number,
    ]),
    personalTaskRemindersDailyRecap: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.number,
    ]),
  }),
  savedSearchesSelected: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  addGroupSavedSearches: PropTypes.func,
  removeGroupSavedSearches: PropTypes.func,
  addOneSavedSearches: PropTypes.func,
  removeOneSavedSearches: PropTypes.func,
  saveDataAfterDeleteSearch: PropTypes.func,
  saveDataAfterDeleteBulkSearch: PropTypes.func,
}

export { SavedSearchesList }
export default connect(SavedSearchesList)
